/**
 * Mobile product list row — image left, content center, action button right.
 *
 * Extracted from `app/(main)/products/ProductsClient.tsx` lines 248–356.
 *
 * Shares its buy-state derivation, title link, badge clusters and action
 * control with `ProductCard.tsx` (see `product-card-model.ts`,
 * `ProductCardTitleLink.tsx`, `ProductCardBadges.tsx`,
 * `ProductCardActions.tsx`). What stays here is what genuinely differs: the
 * plain-`div` row shell — there is no shadcn `Card` and no Radix
 * `AspectRatio` wrapper at this breakpoint — the fixed-width thumbnail, and
 * every class string. The rendered markup of both cards is pinned byte-for-byte
 * by `tests/unit/harness/product-card-dom-baseline.spec.tsx`.
 */

"use client";

import { useState } from "react";
import { Expand } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ImageWithFallback } from "@/components/ui/image-with-fallback";
import type { Product } from "@/stores/product-store";
import { StaticVideoThumbnail } from "../media/StaticVideoThumbnail";
import { ProductCardActions } from "./ProductCardActions";
import { ProductCardOverlayBadges, ProductCardTagRow } from "./ProductCardBadges";
import { ProductCardTitleLink } from "./ProductCardTitleLink";
import { deriveProductCardModel } from "./product-card-model";
import { isVideoKey } from "../product-card-utils";

export function ProductCardMobile({
  product,
  onClick,
  onAddToCart,
  onZoom,
}: {
  product: Product;
  onClick: () => void;
  onAddToCart: (p: Product, e: React.MouseEvent) => void;
  onZoom: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const model = deriveProductCardModel(product);
  const { priceLabel, isOutOfStock } = model;
  // No `role="button"` — see the matching comment in ProductCard.tsx for the
  // nested-interactive a11y rationale. Mouse "click anywhere" works via the
  // div's onClick; keyboard users open details via the title button below.
  return (
    <div
      data-testid="product-card"
      onClick={onClick}
      className="flex cursor-pointer items-stretch gap-2 min-[400px]:gap-3 overflow-hidden rounded-xl border border-border/60 bg-card transition-all active:bg-muted/30"
    >
      <div className="relative aspect-square w-24 min-[400px]:w-28 shrink-0 overflow-hidden bg-muted">
        {!loaded && <Skeleton className="absolute inset-0" />}
        {isVideoKey(product.imageKey) ? (
          <StaticVideoThumbnail
            src={product.imageUrlRaw || product.imageUrl}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
            onLoadedMetadata={() => setLoaded(true)}
            aria-label={product.title}
          />
        ) : (
          <ImageWithFallback
            src={product.imageUrlRaw || product.imageUrl}
            alt={product.title}
            fill
            sizes="112px"
            onLoad={() => setLoaded(true)}
            className={`object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
            fallbackClassName="absolute inset-0"
          />
        )}
        <ProductCardOverlayBadges
          product={product}
          isOutOfStock={isOutOfStock}
          layout="row"
        />
        {/* Lightbox zoom icon */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onZoom();
          }}
          className="absolute bottom-1 right-1 flex h-11 w-11 items-center justify-center rounded-full bg-card/90 text-foreground shadow-sm backdrop-blur-sm"
          aria-label="View full image"
        >
          <Expand className="h-4 w-4" />
        </button>
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-1 py-2 pr-2 min-[400px]:py-2.5 min-[400px]:pr-3">
        <div>
          <ProductCardTagRow product={product} layout="row" />
          <h3 className="mt-1 font-serif text-sm font-semibold text-foreground">
            <ProductCardTitleLink
              product={product}
              onOpen={onClick}
              // Single-line truncation: long titles collapse to "First few words…"
              // instead of wrapping to a second row. Native tooltip still
              // surfaces the full text on long-press. The whole card row is
              // already the tap target via the parent onClick, so we don't
              // need a 44px touch-floor here — adding one with negative
              // margins makes the link bleed up into the badge row above.
              className="block w-full truncate text-left text-inherit hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:rounded-sm"
            />
          </h3>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-base font-bold tabular-nums text-foreground">
            {priceLabel}
          </p>
          <div onClick={(e) => e.stopPropagation()}>
            <ProductCardActions
              product={product}
              model={model}
              onOpen={onClick}
              onAddToCart={onAddToCart}
              className="h-11 w-11 shrink-0 rounded-full bg-primary hover:bg-primary/90"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
